import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../hooks';
import Grid from '../../../components/common/grid';
import ContextMenu from '../../../components/common/grid/contextMenu';
import { gridKeys } from '../../../constants/gridKeys';
import { RootState } from '../../../store';
import RecordResultMessage from '../../../components/common/recordResult';
import { Common } from '../../../components';
import Panel from '../../../components/common/grid/managed/panel';
import { systemSettingsRoutes } from '../../systemSettings/constants';

import {
  clearFranchiseErrors,
  getFranchiseListData,
  getFranchiseListDataBySearchTerm,
  getFranchiseMetadata
} from '../../franchises/store/franchiseActions';
import { WriteOnly } from '../../../components/common/grid/WriteOnly';
import { CONTEXT_MENU_KEYS } from '../../../constants/contextMenuEntries';

const getPanel = (onCreate: () => void) => (
  <Panel name="right">
    <WriteOnly>
      <Common.Button primary onClick={onCreate}>
        <Translate value="franchises.page.view.create-action" />
      </Common.Button>
    </WriteOnly>
  </Panel>
);

/**
 * Franchises list content - designed to be embedded in SystemSettings container.
 * Does NOT include BasePageView or h1 - those come from the parent container.
 */
const FranchisesContent = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const franchiseState = useSelector((s: RootState) => (s as any).franchise);
  const session = useSelector((s: RootState) => s.session);

  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          getMetadata: getFranchiseMetadata,
          getListData: getFranchiseListData,
          clearRecordMessages: clearFranchiseErrors,
          getListDataBySearchTerm: getFranchiseListDataBySearchTerm
        },
        dispatch
      ),
    [dispatch]
  );

  const contextMenu = (
    <ContextMenu>
      <ContextMenu.Item
        key={CONTEXT_MENU_KEYS.FRANCHISES.EDIT.key}
        label={I18n.t('franchises.page.view.edit-action')}
        onClick={(_id: number, item: any) => {
          if (item?.id) {
            navigate(CONTEXT_MENU_KEYS.FRANCHISES.EDIT.getUrl(item, {}));
          }
        }}
      />
      <ContextMenu.Item
        key={CONTEXT_MENU_KEYS.FRANCHISES.HUB.key}
        label={I18n.t('franchises.page.view.hub-action')}
        onClick={(_id: number, item: any) => {
          if (item?.id) {
            // Hub navigates OUTSIDE of system settings
            navigate(CONTEXT_MENU_KEYS.FRANCHISES.HUB.getUrl(item, {}));
          }
        }}
      />
      <ContextMenu.Item
        key={CONTEXT_MENU_KEYS.FRANCHISES.CAMPAIGNS.key}
        label={I18n.t('franchises.page.view.campaigns-action')}
        onClick={(_id: number, item: any) => {
          if (item?.id) {
            navigate(CONTEXT_MENU_KEYS.FRANCHISES.CAMPAIGNS.getUrl(item, {}));
          }
        }}
      />
      <ContextMenu.Item
        key={CONTEXT_MENU_KEYS.FRANCHISES.EVENTS.key}
        label={I18n.t('franchises.page.view.events-action')}
        onClick={(_id: number, item: any) => {
          if (item?.id) {
            navigate(CONTEXT_MENU_KEYS.FRANCHISES.EVENTS.getUrl(item, {}));
          }
        }}
      />
    </ContextMenu>
  );

  const panel = getPanel(() => navigate(systemSettingsRoutes.FRANCHISES_CREATE));

  const clearMessages = () =>
    dispatch(clearFranchiseErrors(gridKeys.FRANCHISES) as any);

  return (
    <>
      <RecordResultMessage
        record={franchiseState.record}
        onDismiss={() => dispatch(clearFranchiseErrors('record') as any)}
      />
      <h2>
        <Translate value="franchises.page.view.title" />
      </h2>
      <RecordResultMessage
        record={franchiseState.franchisesList}
        onDismiss={clearMessages}
      />
      <Grid.Managed
        listKey={gridKeys.FRANCHISES}
        list={franchiseState.franchisesList}
        actions={{ ...actions }}
        search
        filtersButton={false}
      >
        {panel}
        {contextMenu}
      </Grid.Managed>
    </>
  );
};

export default FranchisesContent;
